import { useId } from 'react';
import { SelectField, TextField } from '../../../components/ui/TextField';
import {
  CONTROL_PROFILES,
  DISPOSITIONS,
  INSTRUMENT_TYPES,
  INTENDED_TREATMENTS,
  LEGAL_REGIMES,
  SOURCE_FORMS,
} from './admissionProfileLabels';

const inputClass =
  'mt-2 block w-full rounded-lg border border-[#AAC2FE] bg-white px-3 py-3 text-sm text-[#1F2933] transition-colors placeholder:text-[#8D8D8D] focus:border-[#003A70] focus:outline-none focus:ring-2 focus:ring-[#003A70]/15 disabled:bg-white disabled:text-[#8D8D8D]';

const labelClass = 'block text-xs font-semibold text-[#5A5A5A]';

const controlOverride =
  '!mt-2 !rounded-lg !border-[#AAC2FE] !bg-white !px-3 !py-3 !text-sm !text-[#1F2933] !shadow-none placeholder:!text-[#8D8D8D] focus:!border-[#003A70] focus:!ring-2 focus:!ring-[#003A70]/15 disabled:!bg-white disabled:!text-[#8D8D8D]';

const focusRing =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#003A70]/30 focus-visible:ring-offset-2 disabled:opacity-50';

function Help({ id, children }) {
  return (
    <p id={id} className="mt-1 text-xs leading-4 text-[#5A5A5A]">
      {children}
    </p>
  );
}

function FieldError({ id, children }) {
  if (!children) return null;
  return (
    <p id={id} className="mt-1 text-xs text-[#DC3545]" role="alert">
      {children}
    </p>
  );
}

function Section({ title, intro, children }) {
  return (
    <section className="rounded-2xl border border-[#F1F1F1] bg-white p-6 shadow-[6px_6px_54px_rgba(0,0,0,0.03)]">
      <h3 className="text-base font-bold text-[#1F2933]">{title}</h3>
      {intro ? <p className="mt-1 text-xs leading-4 text-[#5A5A5A]">{intro}</p> : null}
      <div className="mt-5 space-y-5">{children}</div>
    </section>
  );
}

function ChoiceGroup({ legend, helpId, help, errorId, error, children }) {
  const describedBy = [helpId, error ? errorId : null].filter(Boolean).join(' ') || undefined;
  return (
    <fieldset aria-describedby={describedBy}>
      <legend className="text-xs font-semibold text-[#5A5A5A]">{legend}</legend>
      {help ? <Help id={helpId}>{help}</Help> : null}
      <div className="mt-3 flex flex-col gap-3">{children}</div>
      <FieldError id={errorId}>{error}</FieldError>
    </fieldset>
  );
}

function Choice({ name, value, checked, disabled, onChange, label }) {
  return (
    <label className="flex items-center gap-2.5 text-sm text-[#1F2933]">
      <input
        type="checkbox"
        className="h-4 w-4 shrink-0 rounded accent-[#16335D]"
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
      />
      <span>{label}</span>
    </label>
  );
}

function toggle(list, value) {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

function BlankSelect({ id, label, value, disabled, onChange, options }) {
  return (
    <SelectField
      id={id}
      label={label}
      labelClassName={labelClass}
      selectClassName={controlOverride}
      value={value || ''}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
    >
      <option value="">Not set</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </SelectField>
  );
}

function StringRows({
  label,
  values,
  disabled,
  onChange,
  addLabel,
  rowLabel,
  removeLabel,
  errorPrefix,
  errors,
}) {
  return (
    <div>
      <p className="text-xs font-semibold text-[#5A5A5A]">{label}</p>
      <ul className="mt-2 space-y-2">
        {values.map((value, index) => {
          const error = errors[`${errorPrefix}-${index}`];
          const inputId = `${errorPrefix}-${index}`;
          return (
            <li key={inputId} className="flex flex-col gap-2 sm:flex-row sm:items-start">
              <div className="min-w-0 flex-1">
                <TextField
                  id={inputId}
                  label={`${rowLabel} ${index + 1}`}
                  labelClassName={labelClass}
                  inputClassName={controlOverride}
                  value={value}
                  disabled={disabled}
                  aria-invalid={error ? true : undefined}
                  aria-describedby={error ? `${inputId}-error` : undefined}
                  onChange={(event) => {
                    const next = [...values];
                    next[index] = event.target.value;
                    onChange(next);
                  }}
                />
                <FieldError id={`${inputId}-error`}>{error}</FieldError>
              </div>
              <button
                type="button"
                className={`inline-flex min-h-[40px] items-center justify-center px-1 text-xs font-semibold text-[#5A5A5A] sm:mt-7 ${focusRing}`}
                disabled={disabled}
                onClick={() => onChange(values.filter((_, itemIndex) => itemIndex !== index))}
              >
                {removeLabel} {index + 1}
              </button>
            </li>
          );
        })}
      </ul>
      <button
        type="button"
        className={`mt-3 inline-flex items-center text-xs font-semibold text-[#003A70] ${focusRing}`}
        disabled={disabled}
        onClick={() => onChange([...values, ''])}
      >
        {addLabel}
      </button>
    </div>
  );
}

export function AdmissionProfileForm({
  form,
  onChange,
  onSubmit,
  disabled,
  errors,
  showSave,
  saveLabel = 'Save draft',
}) {
  const baseId = useId();
  const patch = (partial) => onChange({ ...form, ...partial });

  function setSource(key, value) {
    const next = { ...form.source_policy };
    if (!value) delete next[key];
    else next[key] = value;
    patch({ source_policy: next });
  }

  function setParty(key, value) {
    const next = { ...form.party_policy };
    if (!String(value || '').trim()) delete next[key];
    else next[key] = value;
    patch({ party_policy: next });
  }

  function setEvidence(index, partial) {
    const items = form.evidence_items.map((item, itemIndex) =>
      itemIndex === index ? { ...item, ...partial } : item
    );
    patch({ evidence_items: items });
  }

  return (
    <form
      className="space-y-5"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit?.();
      }}
    >
      <Section title="Programme treatment">
        <SelectField
          id={`${baseId}-treatment`}
          label="Intended treatment"
          labelClassName={labelClass}
          selectClassName={controlOverride}
          value={form.intended_treatment}
          disabled={disabled}
          required
          aria-describedby={`${baseId}-treatment-help`}
          onChange={(event) => patch({ intended_treatment: event.target.value })}
        >
          {INTENDED_TREATMENTS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </SelectField>
        <Help id={`${baseId}-treatment-help`}>
          Defines the intended programme treatment for submissions. It does not itself make a record
          authoritative.
        </Help>
        <label className="flex min-h-[40px] items-center gap-2.5 text-sm text-[#1F2933]">
          <input
            type="checkbox"
            className="h-4 w-4 rounded accent-[#16335D]"
            checked={form.mixed_packages_permitted}
            disabled={disabled}
            onChange={(event) => patch({ mixed_packages_permitted: event.target.checked })}
          />
          Mixed packages permitted
        </label>
        <TextField
          id={`${baseId}-label`}
          label="Primary instrument label"
          labelClassName={labelClass}
          inputClassName={controlOverride}
          value={form.primary_instrument_label}
          disabled={disabled}
          maxLength={255}
          aria-invalid={errors.primary_instrument_label ? true : undefined}
          aria-describedby={
            errors.primary_instrument_label
              ? `${baseId}-label-help ${baseId}-label-error`
              : `${baseId}-label-help`
          }
          onChange={(event) => patch({ primary_instrument_label: event.target.value })}
        />
        <Help id={`${baseId}-label-help`}>
          Human-readable label for the expected primary instrument. This is a display label and is
          separate from the controlled instrument type.
        </Help>
        <FieldError id={`${baseId}-label-error`}>{errors.primary_instrument_label}</FieldError>
      </Section>

      <Section title="Instruments & legal regimes">
        <div className="grid gap-8 lg:grid-cols-2">
        <ChoiceGroup
          legend="Allowed instrument types"
          helpId={`${baseId}-instruments-help`}
          help="Controlled instrument types this programme may include. An empty selection is allowed."
        >
          {INSTRUMENT_TYPES.map((option) => (
            <Choice
              key={option.value}
              name="allowed_instrument_types"
              value={option.value}
              label={option.label}
              checked={form.allowed_instrument_types.includes(option.value)}
              disabled={disabled}
              onChange={() =>
                patch({
                  allowed_instrument_types: toggle(form.allowed_instrument_types, option.value),
                })
              }
            />
          ))}
        </ChoiceGroup>
        <ChoiceGroup
          legend="Legal regimes"
          helpId={`${baseId}-regimes-help`}
          help="These are the legal regimes this programme profile may apply. Selecting a regime does not classify a document or mean a record is legally approved."
        >
          {LEGAL_REGIMES.map((option) => (
            <Choice
              key={option.value}
              name="legal_regimes"
              value={option.value}
              label={option.label}
              checked={form.legal_regimes.includes(option.value)}
              disabled={disabled}
              onChange={() =>
                patch({ legal_regimes: toggle(form.legal_regimes, option.value) })
              }
            />
          ))}
        </ChoiceGroup>
        </div>
      </Section>

      <Section title="Control profile">
        <SelectField
          id={`${baseId}-control`}
          label="Control profile"
          labelClassName={labelClass}
          selectClassName={controlOverride}
          value={form.control_profile}
          disabled={disabled}
          aria-describedby={`${baseId}-control-help`}
          onChange={(event) => patch({ control_profile: event.target.value })}
        >
          {CONTROL_PROFILES.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </SelectField>
        <Help id={`${baseId}-control-help`}>
          Defines the programme&apos;s technical control model. It is not itself a legal
          determination.
        </Help>
      </Section>

      <Section
        title="Source policy"
        intro="These keys are read by later admission evaluation. Other keys already stored on this object are kept."
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <BlankSelect
            id={`${baseId}-source-form`}
            label="Required source form"
            value={form.source_policy.required_source_form}
            disabled={disabled}
            options={SOURCE_FORMS}
            onChange={(value) => setSource('required_source_form', value)}
          />
          <BlankSelect
            id={`${baseId}-unknown-source`}
            label="Unknown source form"
            value={form.source_policy.unknown_source_form}
            disabled={disabled}
            options={DISPOSITIONS}
            onChange={(value) => setSource('unknown_source_form', value)}
          />
          <BlankSelect
            id={`${baseId}-mixed-document`}
            label="Mixed document"
            value={form.source_policy.mixed_document}
            disabled={disabled}
            options={DISPOSITIONS}
            onChange={(value) => setSource('mixed_document', value)}
          />
          <BlankSelect
            id={`${baseId}-paper`}
            label="Paper original"
            value={form.source_policy.paper_original}
            disabled={disabled}
            options={DISPOSITIONS}
            onChange={(value) => setSource('paper_original', value)}
          />
          <BlankSelect
            id={`${baseId}-parent`}
            label="Missing parent"
            value={form.source_policy.missing_parent}
            disabled={disabled}
            options={DISPOSITIONS}
            onChange={(value) => setSource('missing_parent', value)}
          />
        </div>
        <label className="flex min-h-[40px] items-center gap-2.5 text-sm text-[#1F2933]">
          <input
            type="checkbox"
            className="h-4 w-4 rounded accent-[#16335D]"
            checked={form.source_policy.real_property_link_required === true}
            disabled={disabled}
            onChange={(event) => {
              const next = { ...form.source_policy };
              if (event.target.checked) next.real_property_link_required = true;
              else delete next.real_property_link_required;
              patch({ source_policy: next });
            }}
          />
          Real property link required
        </label>
      </Section>

      <Section
        title="Party policy"
        intro="These are programme defaults used during later admission evaluation and fact resolution."
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id={`${baseId}-control-party`}
            label="Control party"
            labelClassName={labelClass}
            inputClassName={controlOverride}
            value={form.party_policy.control_party || ''}
            disabled={disabled}
            onChange={(event) => setParty('control_party', event.target.value)}
          />
          <TextField
            id={`${baseId}-capacity`}
            label="Legal capacity"
            labelClassName={labelClass}
            inputClassName={controlOverride}
            value={form.party_policy.legal_capacity || ''}
            disabled={disabled}
            onChange={(event) => setParty('legal_capacity', event.target.value)}
          />
          <TextField
            id={`${baseId}-custodian`}
            label="Custodian"
            labelClassName={labelClass}
            inputClassName={controlOverride}
            value={form.party_policy.custodian || ''}
            disabled={disabled}
            aria-describedby={`${baseId}-custodian-help`}
            onChange={(event) => setParty('custodian', event.target.value)}
          />
        </div>
        <Help id={`${baseId}-custodian-help`}>
          The configured custodian is a programme designation. It does not mean Custodian authority
          has been approved.
        </Help>
      </Section>

      <Section
        title="Evidence rules"
        intro="Additional evidence codes for this programme. This does not replace regime-derived evidence checks."
      >
        <ul className="space-y-3">
          {form.evidence_items.map((item, index) => {
            const error = errors[`evidence-${index}`];
            const codeId = `${baseId}-evidence-${index}`;
            return (
              <li
                key={codeId}
                className="grid gap-3 rounded-lg border border-[#E6EAF0] p-3 sm:grid-cols-[1fr_auto_auto] sm:items-end"
              >
                <div>
                  <TextField
                    id={codeId}
                    label={`Evidence code ${index + 1}`}
                    labelClassName={labelClass}
                    inputClassName={controlOverride}
                    value={item.code}
                    disabled={disabled}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={error ? `${codeId}-error` : undefined}
                    onChange={(event) => setEvidence(index, { code: event.target.value })}
                  />
                  <FieldError id={`${codeId}-error`}>{error}</FieldError>
                </div>
                <label className="flex min-h-[40px] items-center gap-2.5 text-sm text-[#1F2933] sm:mb-1">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded accent-[#16335D]"
                    checked={item.required !== false}
                    disabled={disabled}
                    aria-label={`Required for evidence rule ${index + 1}`}
                    onChange={(event) =>
                      setEvidence(index, { required: event.target.checked ? true : false })
                    }
                  />
                  Required
                </label>
                <button
                  type="button"
                  className={`inline-flex min-h-[40px] items-center justify-center px-1 text-xs font-semibold text-[#5A5A5A] ${focusRing}`}
                  disabled={disabled}
                  onClick={() =>
                    patch({
                      evidence_items: form.evidence_items.filter((_, itemIndex) => itemIndex !== index),
                    })
                  }
                >
                  Remove evidence rule {index + 1}
                </button>
              </li>
            );
          })}
        </ul>
        <button
          type="button"
          className={`inline-flex items-center text-xs font-semibold text-[#003A70] ${focusRing}`}
          disabled={disabled}
          onClick={() =>
            patch({ evidence_items: [...form.evidence_items, { code: '', required: true }] })
          }
        >
          Add evidence rule
        </button>
      </Section>

      <Section
        title="Question policy"
        intro="Fact codes that later count as confirmation requirements. This is not the Originator question screen."
      >
        <StringRows
          label="Always confirmed facts"
          values={form.always_confirmed_facts}
          disabled={disabled}
          onChange={(always_confirmed_facts) => patch({ always_confirmed_facts })}
          addLabel="Add always-confirmed fact"
          rowLabel="Always confirmed fact"
          removeLabel="Remove always-confirmed fact"
          errorPrefix="always"
          errors={errors}
        />
        <StringRows
          label="Required confirmations"
          values={form.required_confirmations}
          disabled={disabled}
          onChange={(required_confirmations) => patch({ required_confirmations })}
          addLabel="Add required confirmation"
          rowLabel="Required confirmation"
          removeLabel="Remove required confirmation"
          errorPrefix="required"
          errors={errors}
        />
      </Section>

      <Section title="Certification">
        <TextField
          id={`${baseId}-attestation`}
          label="Attestation version"
          labelClassName={labelClass}
          inputClassName={controlOverride}
          value={form.attestation_version}
          disabled={disabled}
          maxLength={64}
          required
          aria-invalid={errors.attestation_version ? true : undefined}
          aria-describedby={
            errors.attestation_version
              ? `${baseId}-attestation-help ${baseId}-attestation-error`
              : `${baseId}-attestation-help`
          }
          onChange={(event) => patch({ attestation_version: event.target.value })}
        />
        <Help id={`${baseId}-attestation-help`}>
          Identifier for the certification attestation version recorded later with Originator
          certification.
        </Help>
        <FieldError id={`${baseId}-attestation-error`}>{errors.attestation_version}</FieldError>
      </Section>

      <Section
        title="Advanced"
        intro="Stored with the profile and included in the policy hash. The admission gate does not enforce arbitrary keys in business context or certification policy."
      >
        <JsonField
          id={`${baseId}-business`}
          label="Business context"
          value={form.business_context_text}
          disabled={disabled}
          error={errors.business_context}
          onChange={(business_context_text) => patch({ business_context_text })}
        />
        <JsonField
          id={`${baseId}-certification`}
          label="Certification policy"
          value={form.certification_policy_text}
          disabled={disabled}
          error={errors.certification_policy}
          onChange={(certification_policy_text) => patch({ certification_policy_text })}
        />
      </Section>

      {showSave ? (
        <div className="flex flex-wrap gap-2">
          <button
            type="submit"
            className={`inline-flex min-h-[40px] items-center justify-center rounded-lg bg-[#003A70] px-5 py-2.5 text-sm font-semibold text-white ${focusRing}`}
            disabled={disabled}
          >
            {saveLabel}
          </button>
        </div>
      ) : null}
    </form>
  );
}

function JsonField({ id, label, value, disabled, error, onChange }) {
  return (
    <div>
      <label htmlFor={id} className={labelClass}>
        {label}
      </label>
      <textarea
        id={id}
        className={`${inputClass} min-h-[8rem] font-mono text-[13px] text-[#003A70]`}
        value={value}
        disabled={disabled}
        spellCheck={false}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : `${id}-help`}
        onChange={(event) => onChange(event.target.value)}
      />
      <Help id={`${id}-help`}>JSON object only. Clear this section with {'{}'}.</Help>
      <FieldError id={`${id}-error`}>{error}</FieldError>
    </div>
  );
}
